import { sendToSentry } from 'ComponentLibrary'
import { getIsLocalDev } from 'ComponentLibrary/src/common/environment'
import { DEFAULT_LOCALE, getBrowserLocale, normalizeLocale, SupportedLocale } from 'ClientPortal/src/i18n/supportedLocales'

const CCM_SCRIPT_ID = 'client-portal-ccm'
const CCM_BOOTSTRAP_LOCALE_STORAGE_KEY = 'client-portal.ccm.bootstrap-locale'
const CCM_PENDING_RELOAD_LOCALE_STORAGE_KEY = 'client-portal.ccm.pending-reload-locale'

const CLIENT_PORTAL_CCM_CONFIG = {
    apiKey: '035c1c2a1625b37548c20507f128edeb7d0afcd7ab2dec19',
    domain: '65f9b48236f77ba0f10f6192',
} as const

const SUPPORTED_LOCALE_TO_CCM_LOCALE = {
    'de-DE': 'de_DE',
    'en-US': 'en_US',
} as const satisfies Record<SupportedLocale, string>

function reportSessionStorageError(operation: 'read' | 'write' | 'remove', key: string, error: unknown) {
    if (getIsLocalDev()) {
        // eslint-disable-next-line no-console
        console.warn(`[ClientPortal CCM] Failed to ${operation} sessionStorage item "${key}"`, error)
    }

    sendToSentry(new Error(`Failed to ${operation} ClientPortal CCM sessionStorage item "${key}"`, { cause: error }))
}

function getSessionStorageItem(key: string) {
    try {
        return sessionStorage.getItem(key)
    } catch (error) {
        reportSessionStorageError('read', key, error)
        return null
    }
}

function setSessionStorageItem(key: string, value: string) {
    try {
        sessionStorage.setItem(key, value)
        return true
    } catch (error) {
        reportSessionStorageError('write', key, error)
        return false
    }
}

function removeSessionStorageItem(key: string) {
    try {
        sessionStorage.removeItem(key)
    } catch (error) {
        reportSessionStorageError('remove', key, error)
    }
}

export function getClientPortalBootstrapLocale(): SupportedLocale {
    return normalizeLocale(getSessionStorageItem(CCM_BOOTSTRAP_LOCALE_STORAGE_KEY)) || getBrowserLocale() || DEFAULT_LOCALE
}

export function mapSupportedLocaleToCcmLocale(locale: SupportedLocale) {
    return SUPPORTED_LOCALE_TO_CCM_LOCALE[locale]
}

export function injectClientPortalCcm(locale: SupportedLocale) {
    const existingScript = document.getElementById(CCM_SCRIPT_ID)
    if (existingScript instanceof HTMLScriptElement) {
        existingScript.dataset.locale = locale
        return
    }

    const script = document.createElement('script')
    script.id = CCM_SCRIPT_ID
    script.dataset.locale = locale
    script.referrerPolicy = 'origin'
    // eslint-disable-next-line
    script.src = `https://cloud.ccm19.de/app.js?apiKey=${CLIENT_PORTAL_CCM_CONFIG.apiKey}&domain=${CLIENT_PORTAL_CCM_CONFIG.domain}&lang=${mapSupportedLocaleToCcmLocale(locale)}`
    document.head.appendChild(script)
}

export function bootstrapClientPortalCcm() {
    const locale = getClientPortalBootstrapLocale()
    document.documentElement.lang = locale
    injectClientPortalCcm(locale)
}

export function getInjectedClientPortalCcmLocale(): SupportedLocale | undefined {
    const injectedLocale = document.getElementById(CCM_SCRIPT_ID)?.getAttribute('data-locale')
    return normalizeLocale(injectedLocale)
}

export function syncClientPortalCcmLocale(
    locale: SupportedLocale,
    { reload = () => window.location.reload() }: { reload?: () => void } = {}
) {
    const injectedLocale = getInjectedClientPortalCcmLocale()
    const hasStoredBootstrapLocale = setSessionStorageItem(CCM_BOOTSTRAP_LOCALE_STORAGE_KEY, locale)

    const pendingReloadLocale = normalizeLocale(getSessionStorageItem(CCM_PENDING_RELOAD_LOCALE_STORAGE_KEY))
    if (injectedLocale === locale) {
        if (pendingReloadLocale === locale) {
            removeSessionStorageItem(CCM_PENDING_RELOAD_LOCALE_STORAGE_KEY)
        }
        return
    }

    if (pendingReloadLocale === locale) {
        return
    }

    if (!hasStoredBootstrapLocale) {
        return
    }

    setSessionStorageItem(CCM_PENDING_RELOAD_LOCALE_STORAGE_KEY, locale)
    reload()
}
